import { cloneElement, type JSX } from "react";
import { X } from "lucide-react";
import { twMerge } from "tailwind-merge";
import Text, { TextVariants } from "./Text";

enum SizeVariants {
    small = "small",
    medium = "medium",
}

enum StyleVariants {
    filled = "filled",
    outline = "outline",
}

const SizeVariantsConfig: Record<
    SizeVariants,
    {
        heightClass: string;
        iconClass: string;
        rightIconClass: string;
        textVariant: TextVariants;
        closeButtonSizeClass: string;
        closeButtonIconClass: string;
        dividerHeightClass: string;
    }
> = {
    [SizeVariants.small]: {
        heightClass: "h-6",
        iconClass: "mr-0.5 h-3 w-3",
        rightIconClass: "ml-0.5 h-3 w-3",
        textVariant: TextVariants.smallBody600,
        closeButtonSizeClass: "h-6 pr-1.5",
        closeButtonIconClass: "stroke-stone-400 h-3 w-3",
        dividerHeightClass: "h-3",
    },
    [SizeVariants.medium]: {
        heightClass: "h-8",
        iconClass: "mr-1 h-4 w-4",
        rightIconClass: "ml-1 h-4 w-4",
        textVariant: TextVariants.body500,
        closeButtonSizeClass: "h-8 pr-2",
        closeButtonIconClass: "stroke-stone-400 h-4 w-4",
        dividerHeightClass: "h-4",
    },
};

const StyleVariantsConfig: Record<StyleVariants, { styleClassName: string; closeButtonClass: string }> = {
    [StyleVariants.filled]: {
        styleClassName: "bg-stone-100",
        closeButtonClass: "py-1.5",
    },
    [StyleVariants.outline]: {
        styleClassName: "bg-white border border-stone-200 py-[3px]",
        closeButtonClass: "border-l-0 border-stone-200",
    },
};

// We make the base style have a y-padding of 3px to default to the filled version which doesn't have a border.
// The outline version adds a border so we need to adjust the padding to keep the same overall height.
// Another important note is that we set min-w-0 here to allow the chip to shrink properly when placed inside a flex container that needs
// the chip text to truncate.
const baseClassName = "group relative flex items-center px-1.5 min-w-0";

type ChipProps = {
    text: string | JSX.Element;
    size?: keyof typeof SizeVariants;
    variant?: keyof typeof StyleVariants;

    // Function to call when the close (X) button is clicked
    // This will render the close button on the right side of the chip
    onClose?: () => void;

    // Function to call when the chip is clicked
    // This will add the hover effect to the chip
    onClick?: (e?: React.MouseEvent<HTMLDivElement>) => void;

    // The icon to display on the left side of the chip
    // Any className that you set on the icon will be respected in the final result, but is not necessary when using the default styles
    icon?: JSX.Element;

    // The icon to display on the right side of the chip
    // The same rules apply as the left icon
    rightIcon?: JSX.Element;

    // Use this prop for adjusting spacing around the chip
    className?: string;

    // READ THIS FOR CUSTOM CHIP COLORS
    // Use this prop if you want to use custom colors
    internalComponentStyles?: {
        // These settings work best if you use the outline variant
        // Don't worry about hover colors, we handle that with an overlay that darkens the background color by 4%
        /*
            Example Custom Colors Usage:
            <Chip
                text="Failed to Send"
                size="small"
                icon={<Info className="stroke-red-800" />}
                internalComponentStyles={{
                    colorClassName: "bg-red-100 border-red-100",
                    textClassName: "text-red-800",
                    iconColorClassName: "stroke-red-800",
                }}
            />
        */

        // Use this prop to set custom background colors and border colors
        // For example, if you wanted a bronze chip with a dark bronze border color, you could pass in "bg-bronze-600 border-bronze-500"
        colorClassName?: string;

        // Use this prop to set custom text styles
        // For example, "text-white" for a filled dark chip
        textClassName?: string;

        // Use this prop to set custom icon colors
        iconColorClassName?: string;

        // Use this prop to set custom divider colors
        dividerColorClassName?: string;
    };
} & React.HTMLAttributes<HTMLDivElement>;

const Chip: React.FC<ChipProps> = ({
    variant = "outline",
    size = "medium",
    text,
    className,
    internalComponentStyles = {
        colorClassName: "",
        textClassName: "",
        iconColorClassName: "",
        dividerColorClassName: "",
    },
    icon,
    rightIcon,
    onClick,
    onClose,
}) => {
    const { heightClass, textVariant, iconClass, rightIconClass, closeButtonSizeClass, closeButtonIconClass, dividerHeightClass } =
        SizeVariantsConfig[size];
    const { styleClassName, closeButtonClass } = StyleVariantsConfig[variant];

    // Use React.cloneElement to apply the icon className from the color styles directly
    const styledIcon = icon ? (
        <span className={twMerge(`inline-flex items-center justify-center`, iconClass)}>
            {cloneElement(icon as React.ReactElement<any>, {
                className: twMerge("stroke-stone-400", (icon.props as any)?.className),
                strokeWidth: 2.5,
            })}
        </span>
    ) : null;

    const styledRightIcon = rightIcon ? (
        <span className={twMerge(`inline-flex items-center justify-center`, rightIconClass)}>
            {cloneElement(rightIcon as React.ReactElement<any>, {
                className: twMerge("stroke-stone-400", (rightIcon.props as any)?.className),
                strokeWidth: 2.5,
            })}
        </span>
    ) : null;

    return (
        <div className={twMerge("flex items-center", heightClass, className)}>
            <div
                className={twMerge(
                    baseClassName,
                    styleClassName,
                    heightClass,
                    onClose ? "rounded-l border-r-0" : "rounded",
                    onClick ? "cursor-pointer" : "",
                    internalComponentStyles.colorClassName,
                )}
                onClick={onClick}
            >
                {/* Overlay for hover effect. This makes whatever background color that is chosen 4% darker instead of have to explicitly set a hover color for every color used */}
                {onClick && (
                    <div
                        className={twMerge(
                            "pointer-events-none absolute inset-0 z-10 hidden bg-[#0000000A] group-hover:block",
                            onClose ? "rounded-l-[6px]" : "rounded-[6px]",
                            heightClass,
                        )}
                    />
                )}
                {styledIcon}
                {typeof text === "string" && (
                    <Text
                        variant={textVariant}
                        className={twMerge(
                            "whitespace-nowrap text-stone-600",
                            onClick && "group-hover:text-stone-950",
                            internalComponentStyles.textClassName,
                        )}
                    >
                        {text}
                    </Text>
                )}
                {typeof text !== "string" && text}
                {!onClose && styledRightIcon}
            </div>
            {onClose && (
                <button
                    type="button"
                    onClick={onClose}
                    className={twMerge(
                        "group relative inline-flex items-center justify-center rounded-r",
                        styleClassName,
                        closeButtonSizeClass,
                        closeButtonClass,
                        internalComponentStyles.colorClassName,
                        heightClass,
                    )}
                >
                    <div
                        className={twMerge(
                            "pointer-events-none absolute inset-0 z-10 hidden rounded-r-[6px] bg-[#0000000A] group-hover:block",
                            heightClass,
                        )}
                    />
                    <div className={twMerge("mr-[3px] w-px bg-stone-200", dividerHeightClass, internalComponentStyles.dividerColorClassName)} />
                    <X className={twMerge(closeButtonIconClass, internalComponentStyles.iconColorClassName)} />
                </button>
            )}
        </div>
    );
};

export default Chip;
